import React, { useEffect, useRef, useState } from 'react'
import { OpenInNew } from '@mui/icons-material'
import { observer } from 'mobx-react-lite'
import { Box, Grid, Skeleton, Stack } from '@mui/material'
import { DateTime } from 'luxon'
import { useRouteMatch } from 'react-router-dom'

import {
  GetBackgroundColorOfMimeType,
  formatBytes,
  getStrongestWeakest,
  roundNumber,
  useStores,
} from '@trivie/core'
import { ReportsApi, ResourceApi } from '@trivie/core/src/services/api-objects'
import { Button } from '@trivie/ui-web/components/button'
import { Insight } from '@trivie/ui-web/components/insight'
import { Medal } from '@trivie/ui-web/components/medal'
import { SimpleBarChart } from '@trivie/ui-web/components/simple-bar-chart'
import { StrongestWeakest } from '@trivie/ui-web/components/strongest-weakest'
import { Text } from '@trivie/ui-web/components/text'
import { ResourceIcon } from '@trivie/ui-web/components/resource-icon'

import { TableHelper } from '../../../../tables'
import { QuizResultsTable } from '../../../../tables/quiz-results-table'
import { Panel } from '@trivie/ui-web/components/panel/panel'

export const QuizOverview = observer(() => {
  const { manageQuizStore, manageUserStore } = useStores()
  const { freeTier } = manageUserStore
  const {
    getQuizOverview,
    quizStats,
    getKnowledgeGaps,
    medalStats,
    getMedalStats,
    knowledgeGaps,
    selectedQuiz,
    setSelectedQuiz,
  } = manageQuizStore

  const match = useRouteMatch<any>('/content/quizzes/:id/:tab')
  const quizID = Number(match?.params?.id)

  const [tableHelper] = useState(
    new TableHelper('list_quiz_results', 'quiz_results', 'quiz_results', quizID),
  )

  const [resources, setResources] = useState([])

  const [loadingOverview, setLoadingOverview] = useState(true)
  const [loadingKnowledgeGaps, setLoadingKnowledgeGaps] = useState(true)
  const [loadingMedals, setLoadingMedals] = useState(true)
  const [loadingResources, setLoadingResources] = useState(true)
  const [loadingResults, setLoadingResults] = useState(true)

  const overviewRef = useRef<any>()
  const knowledgeGapRef = useRef<any>()
  const medalRef = useRef<any>()

  useEffect(() => {
    load()
    return () => {
      cancelRequests()
    }
  }, [])

  const cancelRequests = () => {
    if (overviewRef.current) {
      overviewRef.current.cancel()
    }
    if (knowledgeGapRef.current) {
      knowledgeGapRef.current.cancel()
    }
    if (medalRef.current) {
      medalRef.current.cancel()
    }
  }

  const load = () => {
    cancelRequests()

    setLoadingOverview(true)
    setLoadingKnowledgeGaps(true)
    setLoadingMedals(true)
    setLoadingResources(true)
    setLoadingResults(true)

    const knowledgeGapCancel = ReportsApi.getCancelToken()
    knowledgeGapRef.current = knowledgeGapCancel
    getKnowledgeGaps(
      quizID,
      DateTime.local(2021).startOf('day').toJSDate().toISOString(),
      DateTime.local().endOf('day').toJSDate().toISOString(),
      knowledgeGapCancel.token,
    )
      .then(() => setLoadingKnowledgeGaps(false))
      .catch((e) => setLoadingKnowledgeGaps(true))

    const overviewCancel = ReportsApi.getCancelToken()
    overviewRef.current = overviewCancel
    getQuizOverview(
      quizID,
      DateTime.local(2021).startOf('day').toJSDate().toISOString(),
      DateTime.local().endOf('day').toJSDate().toISOString(),
      overviewCancel.token,
    )
      .then(() => setLoadingOverview(false))
      .catch((e) => setLoadingOverview(true))

    const medalCancel = ReportsApi.getCancelToken()
    medalRef.current = medalCancel
    getMedalStats(quizID, medalCancel.token)
      .then(() => setLoadingMedals(false))
      .catch((e) => setLoadingMedals(true))

    loadResults()
    loadResources()
  }

  const loadResources = async () => {
    setLoadingResources(true)
    const resourceResponse = await ResourceApi.getAllResources(
      'quizzes',
      false,
      [
        { field: 'source_title' },
        { field: 'source_file_type' },
        { field: 'source_file_size' },
        { field: 'source_filename_url' },
      ],
      'list_quiz_sources',
      quizID,
    ).finally(() => setLoadingResources(false))

    setResources(resourceResponse.data.rows)
  }

  const renderKnowledgeGain = (knowledge: number) => {
    let kg = '0'
    if (knowledge < 0) {
      kg = `-${knowledge}`
    } else if (knowledge > 0) {
      kg = `+${knowledge}`
    }
    return kg
  }

  const [scores, setScores] = useState([
    { x: 1, y: 0 },
    { x: 2, y: 0 },
    { x: 3, y: 0 },
    { x: 4, y: 0 },
    { x: 5, y: 0 },
    { x: 6, y: 0 },
  ])

  const loadResults = () => {
    tableHelper.load().then(() => {
      setLoadingResults(false)
      const s = [
        { x: 1, y: 0 },
        { x: 2, y: 0 },
        { x: 3, y: 0 },
        { x: 4, y: 0 },
        { x: 5, y: 0 },
        { x: 6, y: 0 },
      ]
      tableHelper.rows.map((row) => {
        if (row.result_score < 0.6) {
          s[0].y += 1
        } else if (row.result_score >= 0.6 && row.result_score < 0.7) {
          s[1].y += 1
        } else if (row.result_score >= 0.7 && row.result_score < 0.8) {
          s[2].y += 1
        } else if (row.result_score >= 0.8 && row.result_score < 0.9) {
          s[3].y += 1
        } else if (row.result_score >= 0.9 && row.result_score < 1) {
          s[4].y += 1
        } else if (row.result_score === 1) {
          s[5].y += 1
        }
      })
      setScores(s)
    })
  }

  const weakestQuestions = getStrongestWeakest(knowledgeGaps?.questions ?? []).weakest
  const strongestQuestions = getStrongestWeakest(knowledgeGaps?.questions ?? []).strongest

  return (
    <>
      {selectedQuiz && (
        <Text component="h2" mb="16px" text="Statistics" variant="labelEmphasized" />
      )}
      <Grid container spacing={2} style={{ marginBottom: 40 }}>
        <>
          {!selectedQuiz ? (
            <Box height={115} sx={{ width: '100%' }}>
              <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
            </Box>
          ) : (
            <>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Panel fill>
                  <Insight
                    titleVariant="titleEmphasized"
                    title="Times Completed"
                    tooltip="Number of times this quiz has been completed"
                    value={`${selectedQuiz?.attempts}`}
                  />
                </Panel>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Panel fill>
                  <Insight
                    titleVariant="titleEmphasized"
                    title="Unique Respondents"
                    tooltip="Number of unique respondents"
                    value={`${selectedQuiz?.uniqueRespondents}`}
                  />
                </Panel>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Panel fill>
                  <Insight
                    titleVariant="titleEmphasized"
                    title="Average Score"
                    tooltip="Average score for this quiz"
                    value={`${roundNumber(selectedQuiz.pctCorrect * 100)}%`}
                  />
                </Panel>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Panel fill>
                  <Insight
                    titleVariant="titleEmphasized"
                    title="Average Completion Time"
                    tooltip="Average time users took to complete this quiz"
                    value={`${selectedQuiz?.averageTime} Seconds`}
                  />
                </Panel>
              </Grid>
            </>
          )}
          {loadingOverview ? (
            <Box height={115} sx={{ width: '100%' }}>
              <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
            </Box>
          ) : (
            <>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Panel fill>
                  <Insight
                    titleVariant="titleEmphasized"
                    title="Baseline Knowledge"
                    tooltip="Baseline knowledge tooltip"
                    value={quizStats ? `${quizStats.starting_knowledge.toFixed(0) ?? 0}%` : '0%'}
                  />
                </Panel>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Panel fill>
                  <Insight
                    titleVariant="titleEmphasized"
                    title="Current Knowledge"
                    tooltip="Current knowledge tooltip"
                    value={quizStats ? `${quizStats.current_knowledge.toFixed(0) ?? 0}%` : '0%'}
                  />
                </Panel>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Panel fill>
                  <Insight
                    titleVariant="titleEmphasized"
                    title="Knowledge Gain"
                    tooltip="Knowledge Gain tooltip"
                    value={
                      quizStats
                        ? `${renderKnowledgeGain(Math.round(quizStats.knowledge_gain))}%`
                        : '0%'
                    }
                  />
                </Panel>
              </Grid>
            </>
          )}

          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            {loadingMedals ? (
              <Box height={446} sx={{ width: '100%', display: 'flex' }}>
                <Skeleton
                  sx={{ transform: 'none', height: '100%', width: '100%', borderRadius: '20px' }}
                />
              </Box>
            ) : (
              <Panel fill title="Medals">
                {medalStats
                  .slice()
                  .sort((a, b) => Number(a?.order) - Number(b?.order))
                  .map((stat) => {
                    return (
                      <Stack
                        direction="row"
                        alignItems="center"
                        useFlexGap
                        gap={2}
                        left="-6px"
                        position="relative"
                        key={stat.level}
                      >
                        <Medal hideRecall size={51} level={stat.level} />
                        <Stack>
                          {stat.percent === 100 && stat.level === null ? null : (
                            <Text
                              variant="headline"
                              text={`${Math.round(stat.percent).toFixed(0)}%`}
                            />
                          )}
                          <Text
                            variant="body2Medium"
                            style={{ color: '#9098A3' }}
                            text={
                              stat.level === null
                                ? stat.percent === 100
                                  ? `No users have taken this quiz yet`
                                  : `${stat.user_count} user${
                                      stat.user_count > 1 || stat.user_count === 0 ? 's' : ''
                                    } ${stat.user_count === 1 ? 'has' : 'have'} not earned a medal`
                                : `${stat.user_count} user${
                                    stat.user_count > 1 || stat.user_count === 0 ? 's' : ''
                                  } earned a ${stat.level} medal`
                            }
                          />
                        </Stack>
                      </Stack>
                    )
                  })}
              </Panel>
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 8 }}>
            {loadingResults ? (
              <Box height={446} sx={{ width: '100%', display: 'flex' }}>
                <Skeleton
                  sx={{ width: '100%', transform: 'none', height: '100%', borderRadius: '20px' }}
                />
              </Box>
            ) : (
              <Panel title="Score Distribution">
                <Box height={360}>
                  <SimpleBarChart
                    data={scores}
                    loading={loadingResults}
                    tickValues={['<60%', '>60%', '>70%', '>80%', '>90%', '100%']}
                  />
                </Box>
              </Panel>
            )}
          </Grid>
          {loadingKnowledgeGaps ? (
            <Grid size={{ xs: 12, sm: 12, md: 6 }}>
              <Box height={296} flex={1} sx={{ width: '100%' }}>
                <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
              </Box>
            </Grid>
          ) : weakestQuestions?.length ? (
            <Grid size={{ xs: 12, sm: 12, md: 6 }}>
              <StrongestWeakest
                style={{ height: '100%' }}
                type="Weakest"
                variant="small"
                questions={weakestQuestions}
              />
            </Grid>
          ) : null}
          {loadingKnowledgeGaps ? (
            <Grid size={{ xs: 12, sm: 12, md: 6 }}>
              <Box height={296} flex={1} sx={{ width: '100%' }}>
                <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
              </Box>
            </Grid>
          ) : strongestQuestions?.length ? (
            <Grid size={{ xs: 12, sm: 12, md: 6 }}>
              <StrongestWeakest
                style={{ height: '100%' }}
                type="Strongest"
                variant="small"
                questions={strongestQuestions}
              />
            </Grid>
          ) : null}
        </>
        <Grid size={12}>
          {!loadingResults && (
            <Text component="h2" mb="16px" text="Results" variant="labelEmphasized" />
          )}
          <QuizResultsTable
            restricted={freeTier}
            forceLoading={loadingResults}
            tableHelper={tableHelper}
          />
        </Grid>
      </Grid>
    </>
  )
})

const PANEL: React.CSSProperties = {
  minHeight: 119,
  padding: 20,
  borderRadius: 20,
  background: '#fff',
  border: `1px solid #EFF0F6`,
  display: 'flex',
}
const RESOURCE: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  flexDirection: 'row',
  alignItems: 'center',
  marginBottom: 12,
}

export const QuizzesOverviewTab = (props: any) => {
  const { id } = props

  return {
    name: 'Overview',
    path: `/content/quizzes/${id}/overview`,
    component: () => <QuizOverview />,
  }
}
